import WarningRoundedIcon from "@mui/icons-material/WarningRounded";
import { Box, Button, Paper, Stack, Typography } from "@mui/material";
import { useState } from "react";
import HorizontalFill from "@entrilia/shared/components/HorizontalFill";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import TooltipForDisabledAction from "@entrilia/shared/components/TooltipForDisabledAction";
import { pluralize } from "@entrilia/shared/utilities/stringHelper";
import type { ApprovalConfigurationVersion } from "../../../../api/types/workflowTypes";
import useClientContext from "../../../../context/useClientContext";
import ApprovalConfigurationDialog from "../../workflows/approval-config/ApprovalConfigurationDialog";
import WorkflowApprovalSteps from "../../workflows/approval-config/WorkflowApprovalSteps";
import type { ContactsApproval } from "./contactsApprovalHelper";

interface Props {
  contactsApproval: ContactsApproval;
  onApprovalSaved: (configuration: ApprovalConfigurationVersion) => void;
}

const getWarningText = (contactsApproval: ContactsApproval): string => {
  switch (contactsApproval.status) {
    case "NoAccess":
      return "You don't have permission to view the approval workflow for contact change requests.";
    case "Failed":
      return "Failed to load the approval workflow.";
    case "WorkflowNotActive":
      return "The Contacts Change Request workflow is not active.";
    default:
      return "No approval workflow configured yet.";
  }
};

const ContactsApprovalPanel = ({ contactsApproval, onApprovalSaved }: Props) => {
  const { hasPermissions } = useClientContext();

  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const canEditApproval = hasPermissions(["ManageWorkflows", "ViewOrganizationUsers"]);

  const handleApprovalSaved = (configuration: ApprovalConfigurationVersion) => {
    onApprovalSaved(configuration);
    setIsDialogOpen(false);
  };

  const configuration = contactsApproval.status === "Loaded" ? contactsApproval.configuration : undefined;
  const stepCount = configuration?.steps.length ?? 0;

  return (
    <Paper variant="outlined" sx={{ borderStyle: "dashed", p: 1.5 }}>
      {contactsApproval.status === "Loading" ? (
        <InlineLoader />
      ) : (
        <Stack direction="row" spacing={2} sx={{ alignItems: "center" }}>
          {configuration && stepCount > 0 ? (
            <>
              <Typography variant="caption" color="textSecondary" noWrap>
                {stepCount} {pluralize("step", stepCount)}
              </Typography>
              <Box sx={{ width: "18.5rem" }}>
                <WorkflowApprovalSteps approvalSteps={configuration.steps} userGroups={configuration.groups} />
              </Box>
              <HorizontalFill />
            </>
          ) : (
            <Stack direction="row" spacing={1} sx={{ alignItems: "center", flex: 1 }}>
              <WarningRoundedIcon color="warning" />
              <Typography color="textSecondary">{getWarningText(contactsApproval)}</Typography>
            </Stack>
          )}
          {configuration && (
            <TooltipForDisabledAction
              title={canEditApproval ? undefined : "You don't have permission to edit approval workflows."}
              isActionDisabled={!canEditApproval}
            >
              <Button variant="text" color="primary" disabled={!canEditApproval} onClick={() => setIsDialogOpen(true)}>
                {stepCount > 0 ? "Edit Approval" : "Configure Approval"}
              </Button>
            </TooltipForDisabledAction>
          )}
        </Stack>
      )}
      {isDialogOpen && configuration && (
        <ApprovalConfigurationDialog
          approvalConfiguration={configuration}
          onClose={() => setIsDialogOpen(false)}
          onSaved={handleApprovalSaved}
        />
      )}
    </Paper>
  );
};

export default ContactsApprovalPanel;
